// Import Dependencies
import { RouterProvider } from "react-router";
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react';

// Local Imports
import { BreakpointProvider } from "@/app/contexts/breakpoint/Provider";
import { LocaleProvider } from "@/app/contexts/locale/Provider";
import { SidebarProvider } from "@/app/contexts/sidebar/Provider";
import { ThemeProvider } from "@/app/contexts/theme/Provider";
import { SocketProvider } from "@/app/contexts/socket/SocketProvider";
import router from "./app/router/router";
import CommonLoader from "./components/shared/CommonLoader";
import { useAuthStore } from "./store/useAuthStore";

// ---------------------------------------------------------------------

const queryClient = new QueryClient()

function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const logout = useAuthStore((state) => state.logout);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Listen for storage changes from other tabs
  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      // Check if auth token was removed in another tab
      if (event.key === 'authToken' && event.newValue === null && isAuthenticated) {
        // Logout from this tab
        logout();
      }
      // Also check if storage was completely cleared
      if (event.key === null && isAuthenticated) {
        // Storage was cleared, logout from this tab
        logout();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [isAuthenticated, logout]);

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <LocaleProvider>
          <BreakpointProvider>
            <SidebarProvider>
              <SocketProvider>
                <CommonLoader />
                <RouterProvider router={router} />
              </SocketProvider>
            </SidebarProvider>
          </BreakpointProvider>
        </LocaleProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}

export default App;
